import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Link,
    List,
    ListItemText,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CustomButton from 'components/UI/buttons/CustomButton.style';
import styles from 'features/DefaultHelp.module.css';
import { useCallback, useMemo } from 'react';

const OpenContent = () => {
    const instructions = useMemo(
        () => [
            { key: 'editName', title: 'Edit Yard Check Info' },
            { key: 'startYardCheck', title: 'Start Yard Check' },
        ],
        []
    );

    const renderOverviewSection = useCallback(
        () => (
            <>
                <h4>OVERVIEW</h4>
                <Typography>
                    In the <b>Open</b> state, a Yard Check is initialized but not yet started. This stage allows users to define the basic information
                    needed before actively checking items in the yard. The Open state is typically used for preparation. Once ready, users must click{' '}
                    <b>Start</b> to begin the Yard Check process and transition into the <b>In Progress</b> state.
                </Typography>
            </>
        ),
        []
    );

    const renderControlsSection = useCallback(
        () => (
            <>
                <h4>CONTROLS</h4>
                <Table className={styles['controls-table']}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Actions</TableCell>
                            <TableCell>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <h4>YARD CHECK INFO</h4>
                            </TableCell>
                            <TableCell>Display basic details of the Yard Check, such as name, created date, and creator.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <EditOutlinedIcon fontSize="small" />
                                    <h5>Edit</h5>
                                </Stack>
                            </TableCell>
                            <TableCell>Edit the Yard Check Info prior to starting.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CustomButton customVariant="success">Start</CustomButton>
                            </TableCell>
                            <TableCell>
                                Begin the Yard Check. This action transitions the check into the <b>In Progress</b> state.
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CustomButton customVariant="error">Cancel</CustomButton>
                            </TableCell>
                            <TableCell>Close the Yard Check details and return to the Yard Check List.</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </>
        ),
        []
    );

    const renderInstructionContent = useCallback((key) => {
        switch (key) {
            case 'editName':
                return (
                    <Box className={styles['instruction-child-container']}>
                        <List className={styles['unordered-list']}>
                            <ListItemText>
                                1. Click on <EditOutlinedIcon fontSize="small" /> beside Yard Check Name to prompt the editing modal.
                            </ListItemText>
                            <ListItemText>2. Enter new Yard Check Name.</ListItemText>
                            <ListItemText>3. Press Enter to save changes.</ListItemText>
                        </List>
                    </Box>
                );
            case 'startYardCheck':
                return (
                    <Box className={styles['instruction-child-container']}>
                        <List className={styles['unordered-list']}>
                            <ListItemText>
                                1. Click on the <CustomButton customVariant="success">Start</CustomButton> to initiate a yard check.
                            </ListItemText>
                            <ListItemText>
                                2. Once started, a yard check will enter the <b>In Progress</b> state, enabling asset verification actions.
                            </ListItemText>
                        </List>
                    </Box>
                );
            default:
                return null;
        }
    }, []);

    const renderInstructionsSection = useCallback(
        () => (
            <>
                <h4>INSTRUCTIONS</h4>
                <Box className={styles['instructions-accordion-container']}>
                    {instructions.map((instruction, index) => (
                        <Accordion key={instruction.key} defaultExpanded={index === 0} disableGutters className={styles['instruction-accordion']}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`${instruction.key}-content`}
                                id={`${instruction.key}-header`}
                                className={styles['instruction-accordion-summary']}
                            >
                                <Typography className={styles['instruction-header']}>{instruction.title}</Typography>
                            </AccordionSummary>
                            <AccordionDetails className={styles['instruction-accordion-details']}>
                                {renderInstructionContent(instruction.key)}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </>
        ),
        [instructions, renderInstructionContent]
    );

    const renderDivider = useCallback(() => <Divider className={styles['divider']} />, []);

    const renderFooter = useCallback(
        () => (
            <Stack className={styles['footer']}>
                <span>
                    {`For further support, email us at `}
                    <Link href="mailto:cs@birdseye.ca" underline="none">
                        cs@birdseye.ca
                    </Link>
                    {'.'}
                </span>
            </Stack>
        ),
        []
    );

    return (
        <Stack className={styles['help-dialog-container']}>
            <Box className={styles['help-dialog-content-container']}>
                {renderOverviewSection()}
                {renderDivider()}
                {renderControlsSection()}
                {renderDivider()}
                {renderInstructionsSection()}
            </Box>
            {renderFooter()}
        </Stack>
    );
};

export default OpenContent;
